import { createContext, provide } from '@lit/context'
import { LitElement, PropertyValues, css, html } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import type { EPNavListItem } from './ep-nav-list-item';

export type EPNavListContext = {
  rootNavList: EPNavList;
  selectedItemID?: string;
  hasSearchBar: boolean;
  selectable: boolean;
};

export const context = createContext<EPNavListContext>(Symbol('ep-nav-list-context'));

/**
 * A subnav list
 */
@customElement('ep-nav-list-subnav')
export class EPNavListSubNav extends LitElement {

  render() {
    return html`
      <nav>
        <ul>
          <slot></slot>
        </ul>
      </nav>
    `
  }

  static styles = css`
    :host {
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
      --color-soft-black: rgb(36, 41, 47);
      --vertical-padding: 8px;
      --indent-count-list: 0;
    }

    :host([slot=subnav]) {
      --vertical-padding: 0;
    }

    * {
      box-sizing: border-box;
    }

    nav {
      font-family: var(--font-prose);
      line-height: 1.5;
      color: var(--color-soft-black);
    }

    ul {
      margin: 0px;
      padding-inline-start: 0px;
      padding-top: var(--vertical-padding);
      padding-bottom: var(--vertical-padding);
    }

    ul[data-is-subnav=true] {
      padding-top: 0px;
      padding-bottom: 0px;
    }

    [part=content] {
      display: contents;
    }

    ul[data-is-subnav=true] [part=content] {
      display: block;
      padding-left: calc(32px - 8px);
    }

    ::slotted(ep-nav-list-item:not(:first-of-type)) {
      --divider-color: rgba(208,215,222,0.48);
    }

    ::slotted(ep-nav-list-group:not(:first-child)) {
      margin-top: 8px;
    }

    ::slotted(ep-nav-list-item) {
      --indent-count-item: var(--indent-count-list);
    }
  `
} 

/**
 * A nav list
 */
@customElement('ep-nav-list')
export class EPNavList extends EPNavListSubNav {

  @provide({ context: context })
  @property({ attribute: false })
  provider: EPNavListContext = {
    rootNavList: this,
    selectedItemID: undefined,
    hasSearchBar: false,
    selectable: false,
  };

  @property({ type: Boolean, reflect: true })
  selectable: boolean = false;

  @property({ type: Boolean, attribute: 'subnavs-start-closed', reflect: true })
  subNavStartClosed = false;

  @query('slot')
  slotElement?: HTMLSlotElement;

  @query('slot[name=empty]')
  emptySlotElement?: HTMLSlotElement;

  @property({ reflect: false })
  selectedItemID?: string;

  get selectedValue(): string | undefined {
    return this.descendants.find(item => item.id === this.selectedItemID)?.value;
  }

  get descendants(): EPNavListItem[] {
    return Array.from(this.querySelectorAll('ep-nav-list-item'));
  }

  clearSelection() {
    for(let child of this.descendants) {
      if (child.selected) {
        child.selected = false;
      }
    }
    this.selectedItemID = undefined;
  }

  // protected updated(_changedProperties: PropertyValues): void {
  //   super.updated(_changedProperties);

  //   for(let child of this.descendants) {
  //     if ()
  //   }
  // }

  render() {
    return html`
      <nav>
        <ul>
          <slot @ep-nav-list-select=${this._handleNavListSelect} @slotchange=${this._handleSlotChange}></slot>
          <slot name="empty"></slot>
        </ul>
      </nav>
    `
  }

  /**
   * Change the provider state whenever a child item is selected 
   */
  private _handleNavListSelect(event: CustomEvent<EPNavListItem>) {
    //event.stopPropagation();
    this.provider = {
      ...this.provider,
      selectedItemID: event.detail.id,
    }
    this.selectedItemID = event.detail.id;
  }

  private _handleSlotChange() {
    const slotted = this.slotElement?.assignedElements() ?? [];
    this.provider = {
      ...this.provider,
      hasSearchBar: slotted.findIndex(v => v.tagName.toLowerCase() === 'ep-nav-list-search') >= 0
    }
  }

  protected updated(_changedProperties: PropertyValues<this>) {
    super.updated(_changedProperties);
    if (this.selectable !== this.provider.selectable) {
      this.provider = {
        ...this.provider,
        selectable: this.selectable,
      }
    }
  }

  // static styles = css`
  //   :host {
  //   }
  // `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-nav-list': EPNavList
  }
}
